import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, X, Loader2 } from 'lucide-react';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from '@react-google-maps/api';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { ProfissionalCard } from '../components/ProfissionalCard';
import api from '../lib/api';

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY || '';

// ---- Skeleton ----
function SkeletonCard() {
  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm animate-pulse">
      <div className="flex gap-4">
        <div className="w-16 h-16 bg-gray-200 rounded-2xl" />
        <div className="flex-1 space-y-2.5">
          <div className="h-4 bg-gray-200 rounded w-3/4" />
          <div className="h-3 bg-gray-200 rounded w-1/2" />
          <div className="h-3 bg-gray-200 rounded w-2/3" />
        </div>
      </div>
    </div>
  );
}

// ---- Mapa ----
function MapaProfissionais({ profissionais }) {
  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: MAPS_KEY });
  const [selecionado, setSelecionado] = useState(null);

  const comCoordenadas = profissionais.filter(p => p.lat && p.lng);

  const center = useMemo(() => {
    if (comCoordenadas.length > 0) {
      const lat = comCoordenadas.reduce((s, p) => s + p.lat, 0) / comCoordenadas.length;
      const lng = comCoordenadas.reduce((s, p) => s + p.lng, 0) / comCoordenadas.length;
      return { lat, lng };
    }
    return { lat: -15.78, lng: -47.93 }; // Brasil centro
  }, [comCoordenadas]);

  if (!isLoaded) return (
    <div className="h-[500px] bg-gray-100 rounded-2xl flex items-center justify-center">
      <Loader2 className="animate-spin text-gray-400" size={28} />
    </div>
  );

  return (
    <div className="rounded-2xl overflow-hidden border border-gray-200 shadow-sm">
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '500px' }}
        center={center}
        zoom={comCoordenadas.length > 0 ? 13 : 5}
        options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: true }}
      >
        {comCoordenadas.map(p => (
          <Marker
            key={p.id}
            position={{ lat: p.lat, lng: p.lng }}
            title={p.nome}
            onClick={() => setSelecionado(p)}
          />
        ))}

        {selecionado && (
          <InfoWindow
            position={{ lat: selecionado.lat, lng: selecionado.lng }}
            onCloseClick={() => setSelecionado(null)}
          >
            <div className="p-1 max-w-52">
              <div className="flex items-center gap-2 mb-1.5">
                {selecionado.avatarUrl
                  ? <img src={selecionado.avatarUrl} alt="" className="w-9 h-9 rounded-full object-cover" />
                  : <div className="w-9 h-9 rounded-full bg-[#E1F5EE] flex items-center justify-center font-bold text-[#1D9E75]">{selecionado.nome[0]}</div>
                }
                <div>
                  <p className="font-semibold text-gray-900 text-sm leading-tight">{selecionado.nome}</p>
                  {selecionado.bairro && <p className="text-xs text-gray-500">{selecionado.bairro} · {selecionado.cidade}</p>}
                </div>
              </div>
              {selecionado.servicos?.[0] && (
                <p className="text-xs text-gray-600 mb-2">{selecionado.servicos[0].nome}</p>
              )}
              <a
                href={`/profissional/${selecionado.slug}`}
                className="text-xs text-[#1D9E75] font-semibold hover:underline"
              >
                Ver perfil →
              </a>
            </div>
          </InfoWindow>
        )}
      </GoogleMap>

      {profissionais.length > 0 && comCoordenadas.length < profissionais.length && (
        <div className="bg-amber-50 px-4 py-2 text-xs text-amber-700 border-t border-amber-100">
          {profissionais.length - comCoordenadas.length} profissional(is) sem endereço cadastrado não aparecem no mapa.
        </div>
      )}

      {comCoordenadas.length === 0 && profissionais.length > 0 && (
        <div className="bg-gray-50 px-4 py-3 text-sm text-gray-500 text-center border-t border-gray-100">
          Nenhum profissional desta busca tem endereço cadastrado ainda.
        </div>
      )}
    </div>
  );
}

// ---- Página ----
export default function Buscar() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [profissionais, setProfissionais] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [cidades, setCidades] = useState([]);
  const [categorias, setCategorias] = useState([]);

  const q = searchParams.get('q') || '';
  const cidadeParam = searchParams.get('cidade') || '';
  const estadoParam = searchParams.get('estado') || '';
  const categoriaParam = searchParams.get('categoria') || '';
  const bairroParam = searchParams.get('bairro') || '';

  const [searchInput, setSearchInput] = useState(q);
  const [cidadeInput, setCidadeInput] = useState(cidadeParam);
  const [categoriaInput, setCategoriaInput] = useState(categoriaParam);
  const [bairroInput, setBairroInput] = useState(bairroParam);

  useEffect(() => {
    api.get('/cidades').then(res => setCidades(res.data)).catch(() => {});
    api.get('/categorias').then(res => setCategorias(res.data.map(c => c.nome))).catch(() => {});
  }, []);

  useEffect(() => {
    setSearchInput(q);
    setCidadeInput(cidadeParam);
    setCategoriaInput(categoriaParam);
    setBairroInput(bairroParam);
  }, [q, cidadeParam, categoriaParam, bairroParam]);

  const buscar = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (cidadeParam) params.set('cidade', cidadeParam);
      if (estadoParam) params.set('estado', estadoParam);
      if (bairroParam) params.set('bairro', bairroParam);
      if (categoriaParam) params.set('categoria', categoriaParam);
      params.set('page', pg);
      params.set('limit', '24');

      const res = await api.get(`/profissionais?${params}`);
      setProfissionais(res.data.profissionais || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
      setPage(pg);
    } catch {}
    finally { setLoading(false); }
  }, [q, cidadeParam, estadoParam, bairroParam, categoriaParam]);

  useEffect(() => { buscar(1); }, [buscar]);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchInput.trim()) params.set('q', searchInput.trim());
    if (categoriaInput) params.set('categoria', categoriaInput);
    if (cidadeInput) {
      params.set('cidade', cidadeInput);
      const c = cidades.find(x => x.nome === cidadeInput);
      if (c) params.set('estado', c.uf);
    }
    if (bairroInput.trim()) params.set('bairro', bairroInput.trim());
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearchInput(''); setCidadeInput(''); setBairroInput(''); setCategoriaInput('');
    setSearchParams({});
  };

  const temFiltro = q || cidadeParam || bairroParam || categoriaParam;

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />

      {/* Barra de busca */}
      <div className="bg-white border-b border-gray-100 px-4 py-4 sticky top-16 z-40 shadow-sm">
        <div className="max-w-6xl mx-auto">
          <form onSubmit={handleSearch} className="flex gap-2 flex-wrap items-center">
            <div className="relative flex-1 min-w-40">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                placeholder="Que profissional você precisa?"
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 pl-9 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]"
                data-testid="search-input"
              />
            </div>

            <select
              value={categoriaInput}
              onChange={e => setCategoriaInput(e.target.value)}
              className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75] bg-white"
              data-testid="categoria-select"
            >
              <option value="">Todas as categorias</option>
              {categorias.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>

            <select
              value={cidadeInput}
              onChange={e => setCidadeInput(e.target.value)}
              className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75] bg-white"
              data-testid="cidade-select"
            >
              <option value="">Todas as cidades</option>
              {cidades.map(c => (
                <option key={c.id} value={c.nome}>{c.nome}/{c.uf}</option>
              ))}
            </select>

            <div className="relative min-w-36">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={bairroInput}
                onChange={e => setBairroInput(e.target.value)}
                placeholder="Bairro"
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 pl-8 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]"
                data-testid="bairro-input"
              />
            </div>

            <button type="submit" className="bg-[#1D9E75] hover:bg-[#178460] text-white font-medium py-2.5 px-5 rounded-xl text-sm transition-colors" data-testid="search-submit-btn">
              Buscar
            </button>

            {temFiltro && (
              <button type="button" onClick={clearFilters} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 px-2" data-testid="clear-filters-btn">
                <X size={14} /> Limpar
              </button>
            )}
          </form>
        </div>
      </div>

      {/* Resultados */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <p className="text-sm text-gray-500">
            {loading ? 'Buscando...' : (
              <>
                <span className="font-semibold text-gray-900">{total}</span> profissional{total !== 1 ? 'is' : ''} encontrado{total !== 1 ? 's' : ''}
                {q && <span> para "<strong>{q}</strong>"</span>}
                {categoriaParam && <span> · <strong>{categoriaParam}</strong></span>}
                {cidadeParam && <span> em <strong>{cidadeParam}</strong></span>}
                {bairroParam && <span>, bairro <strong>{bairroParam}</strong></span>}
              </>
            )}
          </p>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : profissionais.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search size={24} className="text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhum profissional encontrado</h3>
            <p className="text-gray-500 text-sm mb-6">Tente outros termos ou remova os filtros.</p>
            <button onClick={clearFilters} className="border border-[#1D9E75] text-[#1D9E75] hover:bg-[#E1F5EE] font-medium py-2.5 px-5 rounded-xl transition-colors text-sm" data-testid="clear-filters-empty-btn">
              Remover filtros
            </button>
          </div>
        ) : (
          <>
            {/* Lista */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {profissionais.map(p => <ProfissionalCard key={p.id} profissional={p} />)}
            </div>

            {pages > 1 && (
              <div className="flex justify-center items-center gap-3 mt-10">
                <button onClick={() => buscar(page - 1)} disabled={page === 1}
                  className="border border-gray-200 hover:bg-gray-50 text-gray-700 font-medium py-2 px-5 rounded-xl text-sm disabled:opacity-40 transition-colors" data-testid="prev-page-btn">
                  Anterior
                </button>
                <span className="text-sm text-gray-500">{page} / {pages}</span>
                <button onClick={() => buscar(page + 1)} disabled={page === pages}
                  className="border border-gray-200 hover:bg-gray-50 text-gray-700 font-medium py-2 px-5 rounded-xl text-sm disabled:opacity-40 transition-colors" data-testid="next-page-btn">
                  Próxima
                </button>
              </div>
            )}

            {/* Mapa — sempre visível abaixo da lista */}
            <div className="mt-10">
              <h2 className="text-base font-bold text-gray-900 mb-3" style={{ fontFamily: "'Work Sans', sans-serif" }}>
                Localização no mapa
              </h2>
              <MapaProfissionais profissionais={profissionais} />
            </div>
          </>
        )}
      </div>

      <Footer />
    </div>
  );
}
